import React from 'react';
import { Box, Chip, FormControlLabel, Stack, Switch, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  CheckCircleOutline as CheckIcon,
  ErrorOutline as ErrorIcon,
  HelpOutline as HelpIcon
} from '@mui/icons-material';
import { MONO_FONT } from '../../constants/typography';

export const MAX_JUMP_CHIPS = 8;

// The 3px left rule is the shared "registry verdict" signature (summary + cells).
const toneColor = (theme, tone) => (tone === 'neutral' ? theme.palette.text.secondary : theme.palette[tone].main);

/**
 * Status line for a bound JSON attribute's registry validation (§9.3):
 * not verified / all valid / N of M invalid (+ jump chips) / misconfiguration notes.
 */
const RegistryValidationSummary = ({
  validation,
  isEditing = false,
  modifiedCount = 0,
  onlyInvalid = false,
  onOnlyInvalidChange,
  onJump,
  canJump = () => true,
}) => {
  const instances = Array.isArray(validation?.instances) ? validation.instances : [];
  const invalidCount = validation?.invalidCount ?? instances.length;
  const checked = validation?.checked ?? 0;

  let tone;
  let Icon;
  let message;
  if (!validation) {
    tone = 'neutral';
    Icon = HelpIcon;
    message = 'Non ancora verificato';
  } else if (validation.valid) {
    tone = 'success';
    Icon = CheckIcon;
    message = checked > 0 ? `${checked} valori verificati, tutti validi` : 'Nessun valore da verificare';
  } else if (invalidCount > 0) {
    tone = 'error';
    Icon = ErrorIcon;
    message = `${invalidCount} di ${checked} valori non validi`;
  } else {
    tone = 'error';
    Icon = ErrorIcon;
    message = validation.notes || 'Verifica non riuscita';
  }

  const hasInvalidValues = tone === 'error' && invalidCount > 0;
  const shown = hasInvalidValues ? instances.slice(0, MAX_JUMP_CHIPS) : [];
  const remaining = Math.max(0, invalidCount - shown.length);

  // "Modified but not re-checked" overrides the strip's own colour (left rule +
  // icon) to warning.main, independent of the underlying verdict tone used for
  // the message/chips above — the reviewer just fixed values, save to re-verify.
  const isModifiedPendingRecheck = isEditing && modifiedCount > 0;
  const stripTone = isModifiedPendingRecheck ? 'warning' : tone;

  return (
    <Box
      role="status"
      data-tone={stripTone}
      sx={(theme) => ({
        mb: 1.5,
        px: 1.5,
        py: 1,
        borderLeft: `3px solid ${toneColor(theme, stripTone)}`,
        backgroundColor: alpha(toneColor(theme, stripTone), 0.06),
        borderRadius: '0 4px 4px 0',
      })}
    >
      <Stack direction="row" alignItems="center" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 0.5 }}>
        <Icon fontSize="small" sx={(theme) => ({ color: toneColor(theme, stripTone) })} />
        <Typography variant="body2" sx={{ fontWeight: 500, flexGrow: 1 }}>
          {message}
        </Typography>
        {hasInvalidValues && onOnlyInvalidChange && (
          <FormControlLabel
            sx={{ mr: 0 }}
            control={(
              <Switch
                size="small"
                checked={onlyInvalid}
                onChange={(e) => onOnlyInvalidChange(e.target.checked)}
              />
            )}
            label={<Typography variant="body2">Solo non validi</Typography>}
          />
        )}
      </Stack>

      {shown.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1 }}>
          {shown.map((instance) => {
            const jumpable = Boolean(onJump) && canJump(instance);
            return (
              <Chip
                key={instance.path}
                size="small"
                variant="outlined"
                color="error"
                label={`${instance.path} · ${instance.value ?? '—'}`}
                title={instance.notes || undefined}
                onClick={jumpable ? () => onJump(instance) : undefined}
                sx={{ fontFamily: MONO_FONT, fontSize: '0.75rem' }}
              />
            );
          })}
          {remaining > 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
              {`+${remaining} altri`}
            </Typography>
          )}
        </Box>
      )}

      {isEditing && modifiedCount > 0 && (
        <Typography variant="caption" component="p" sx={{ mt: 0.75, color: 'warning.dark' }}>
          {`${modifiedCount} corretti — salva per riverificare`}
        </Typography>
      )}

      {validation?.truncated && (
        <Typography variant="caption" component="p" color="text.secondary" sx={{ mt: 0.75 }}>
          Mostrati i primi 500 valori non validi — salva per riverificare il resto
        </Typography>
      )}
    </Box>
  );
};

export default RegistryValidationSummary;
